import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/issues/components/IssuesToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';
import axiosInstance from '@/setup/axios/axiosInstance';

import { createDefaultIssueForm, issueStatusOptions } from '../issuesMockData';
import { InventoryIssueFormData, InventoryIssueFormLine, InventoryIssueRow } from '../types';
import { createIssue, updateIssue, getIssueById } from '../redux/IssueCRUD';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';
import useRequestMaterialItems from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialItems';
import { getOrders } from '@/modules/orders/redux/OrderCRUD';
import { createStatus } from '@/modules/settings/redux/SettingsCRUD';
import { fetchTransactionStatusesByModule, type TransactionStatusOption } from '@/modules/finance/utils/transactionStatusOptions';
import IssueForm from './IssueForm';
import IssueOverviewList, { IssueOverviewListHandle } from './IssueOverviewList';
import IssueNeedExportList, { IssueNeedExportListHandle } from './IssueNeedExportList';

const buildFormMetadata = (formData: InventoryIssueFormData, issueId?: string) => ({ formData, issueId });

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

const isListTabType = (type?: string) =>
  type === 'issue-list' || type === 'need-export-list';

interface StockBalanceSnapshot {
  on_hand_qty: number;
  allocated_qty: number;
  available_qty: number;
  uom_name?: string;
}

interface IssueItemUomOption {
  id: string;
  name: string;
}

const extractIssueItemUomOptions = (stockBalanceRecord: any): IssueItemUomOption[] => {
  const options = new Map<string, IssueItemUomOption>();

  const appendOption = (candidate: any) => {
    const id = String(candidate?.id || candidate?.uom_id || candidate?.to_uom_id || candidate?.to_unit_id || '').trim();
    if (!id || options.has(id)) {
      return;
    }

    const name = String(
      candidate?.name ||
      candidate?.uom_name ||
      candidate?.title ||
      candidate?.code ||
      candidate?.uom?.name ||
      candidate?.to_uom?.name ||
      candidate?.to_unit_name ||
      candidate?.to_unit_code ||
      '',
    ).trim();

    options.set(id, { id, name: name || id });
  };

  appendOption(stockBalanceRecord?.uom);
  (stockBalanceRecord?.uom?.conversions || []).forEach((conversion: any) => {
    appendOption(conversion?.to_uom);
    appendOption(conversion?.to_unit);
    appendOption({
      id: conversion?.to_uom_id,
      name: conversion?.to_uom_name,
    });
    appendOption({
      id: conversion?.to_unit_id,
      name: conversion?.to_unit_name || conversion?.to_unit_code,
    });
  });

  return Array.from(options.values());
};

export default function IssuesModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const issueOverviewListRef = useRef<IssueOverviewListHandle>(null);
  const needExportListRef = useRef<IssueNeedExportListHandle>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [apiIssueStatuses, setApiIssueStatuses] = useState<TransactionStatusOption[]>([]);

  useEffect(() => {
    fetchTransactionStatusesByModule('issue')
      .then(setApiIssueStatuses)
      .catch(() => setApiIssueStatuses([]));
  }, []);

  const formStatusOptions = useMemo(() => {
    const source = apiIssueStatuses.length > 0 ? apiIssueStatuses : issueStatusOptions;
    return source
      .filter((option) => option.id !== 'all')
      .map((option) => ({ id: option.id, name: option.name, color: (option as any).color || '' }));
  }, [apiIssueStatuses]);

  const toolbarStatusOptions = useMemo(() => {
    if (apiIssueStatuses.length > 0) {
      return [
        { id: 'all', name: 'Tất cả' },
        ...apiIssueStatuses.map((option) => ({ id: option.id, name: option.name })),
      ];
    }
    return issueStatusOptions;
  }, [apiIssueStatuses]);

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const listCompanyId = listScopeCompanyId || selectedCompanyId;

  const {
    canCreate: canCreateIssues,
    canUpdate: canUpdateIssues,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.issue',
    activeCompanyFilter,
    updateTabTypes: ['update-issue'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 99, title: 'Danh sách phiếu', type: 'issue-list', closable: false, createdAt: Date.now() },
      { id: 0, title: 'Danh sách cần xuất', type: 'need-export-list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateIssues) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateIssues]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_issues_tabs_v5',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateIssues),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-issue', 'update-issue'],
    allowMultipleTabTypes: ['new-issue', 'update-issue'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `PX #${currentTabs.filter((tab) => tab.type === 'new-issue').length + 1}`,
        type: 'new-issue',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultIssueForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryIssueFormData | undefined;
  const activeIssueId = activeTabData?.metadata?.issueId as string | undefined;

  const { warehouseOptions } = useRequestMaterialWarehouses({
    companyId: activeFormData?.company_id || '',
    skip: !activeFormData?.company_id,
  });

  const { itemOptions, loading: itemLoading, addOption: addItemOption } = useRequestMaterialItems({
    companyId: activeFormData?.company_id || '',
    skip: !activeFormData?.company_id,
  });

  const [uomOptionsByLineId, setUomOptionsByLineId] = useState<Record<number, IssueItemUomOption[]>>({});
  const [stockByLineId, setStockByLineId] = useState<Record<number, StockBalanceSnapshot | null>>({});
  const [stockLoadingByLineId, setStockLoadingByLineId] = useState<Record<number, boolean>>({});
  const [stockErrorByLineId, setStockErrorByLineId] = useState<Record<number, boolean>>({});

  const [orderOptions, setOrderOptions] = useState<any[]>([]);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    const companyId = activeFormData?.company_id;
    if (!companyId || companyId === 'global') {
      setOrderOptions([]);
      return;
    }
    let cancelled = false;
    setOrderLoading(true);
    getOrders({ company: companyId, page_size: 200 })
      .then((response) => {
        if (!cancelled) {
          const results = response.data?.results || response.data || [];
          setOrderOptions(Array.isArray(results) ? results : []);
        }
      })
      .catch(() => {
        if (!cancelled) setOrderOptions([]);
      })
      .finally(() => {
        if (!cancelled) setOrderLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeFormData?.company_id]);

  const stockLinesKey = useMemo(() => {
    return (activeFormData?.lines || []).map(line => `${line.item_code}__${line.uom_id}`).join('|');
  }, [activeFormData?.lines]);

  useEffect(() => {
    const companyId = activeFormData?.company_id;
    const storeId = activeFormData?.store_id;
    const currentLines = activeFormData?.lines || [];
    const fallbackUomOptionsByLineId = currentLines.reduce<Record<number, IssueItemUomOption[]>>((accumulator, line) => {
      const matchedItem = itemOptions.find((item) => item.id === line.item_id);
      const fallbackUomId = line.uom_id || matchedItem?.unit_id || '';
      const fallbackUomName = line.uom_name || matchedItem?.unit_name || fallbackUomId;
      accumulator[line.id] = fallbackUomId ? [{ id: fallbackUomId, name: fallbackUomName }] : [];
      return accumulator;
    }, {});

    if (!companyId || !storeId || currentLines.length === 0) {
      setStockByLineId({});
      setStockLoadingByLineId({});
      setStockErrorByLineId({});
      setUomOptionsByLineId(fallbackUomOptionsByLineId);
      return;
    }

    const itemRequests = currentLines.map((line) => {
      const matchedItem = itemOptions.find((item) => item.id === line.item_id);
      return {
        lineId: line.id,
        itemCode: (line.item_code || matchedItem?.code || '').trim(),
        uomId: line.uom_id || matchedItem?.unit_id || '',
        fallbackUomName: line.uom_name || matchedItem?.unit_name || '',
        fallbackUomOptions: fallbackUomOptionsByLineId[line.id] || [],
      };
    });

    const loadingState = itemRequests.reduce<Record<number, boolean>>((accumulator, current) => {
      if (current.itemCode && current.uomId) {
        accumulator[current.lineId] = true;
      }
      return accumulator;
    }, {});

    setUomOptionsByLineId(fallbackUomOptionsByLineId);
    setStockLoadingByLineId(loadingState);

    let cancelled = false;

    void (async () => {
      const uniqueRequests = Array.from(new Map(
        itemRequests
          .filter((request) => request.itemCode && request.uomId)
          .map((request) => [`${request.itemCode}__${request.uomId}`, request]),
      ).values());

      const responseByKey = new Map<string, { snapshot: StockBalanceSnapshot | null; uomOptions: IssueItemUomOption[]; error: boolean }>();

      await Promise.all(uniqueRequests.map(async (request) => {
        const requestKey = `${request.itemCode}__${request.uomId}`;

        try {
          const response = await axiosInstance.get('/inventory/stock-balance/', {
            params: {
              item_code: request.itemCode,
              company_id: companyId,
              store_id: storeId,
              uom_id: request.uomId,
            },
          });

          const rawRecord = Array.isArray(response.data)
            ? response.data[0]
            : Array.isArray(response.data?.results)
              ? response.data.results[0]
              : response.data;
          const quantitySource = rawRecord?.uom || rawRecord?.item || rawRecord;
          const responseUomOptions = rawRecord ? extractIssueItemUomOptions(rawRecord) : [];

          responseByKey.set(requestKey, {
            snapshot: rawRecord
              ? {
                on_hand_qty: Number(quantitySource?.on_hand_qty) || 0,
                allocated_qty: Number(quantitySource?.allocated_qty) || 0,
                available_qty: Number(quantitySource?.available_qty) || 0,
                uom_name: rawRecord.uom_name || rawRecord.uom?.name || request.fallbackUomName,
              }
              : {
                on_hand_qty: 0,
                allocated_qty: 0,
                available_qty: 0,
                uom_name: request.fallbackUomName,
              },
            uomOptions: responseUomOptions.length > 0 ? responseUomOptions : request.fallbackUomOptions,
            error: false,
          });
        } catch (_error) {
          responseByKey.set(requestKey, {
            snapshot: null,
            uomOptions: request.fallbackUomOptions,
            error: true,
          });
        }
      }));

      if (cancelled) {
        return;
      }

      const nextStockByLineId: Record<number, StockBalanceSnapshot | null> = {};
      const nextErrorByLineId: Record<number, boolean> = {};
      const nextUomOptionsByLineId: Record<number, IssueItemUomOption[]> = {};

      itemRequests.forEach((request) => {
        if (!request.itemCode || !request.uomId) {
          nextStockByLineId[request.lineId] = null;
          nextErrorByLineId[request.lineId] = false;
          nextUomOptionsByLineId[request.lineId] = request.fallbackUomOptions;
          return;
        }

        const resolved = responseByKey.get(`${request.itemCode}__${request.uomId}`);
        nextStockByLineId[request.lineId] = resolved?.snapshot || null;
        nextErrorByLineId[request.lineId] = resolved?.error || false;
        nextUomOptionsByLineId[request.lineId] = resolved?.uomOptions || request.fallbackUomOptions;
      });

      setStockByLineId(nextStockByLineId);
      setStockErrorByLineId(nextErrorByLineId);
      setUomOptionsByLineId(nextUomOptionsByLineId);
      setStockLoadingByLineId({});
    })();

    return () => {
      cancelled = true;
    };
  }, [activeFormData?.company_id, activeFormData?.store_id, stockLinesKey, itemOptions]);

  const refreshActiveList = useCallback(
    (options?: { showSuccessMessage?: boolean }) => {
      if (activeTabData?.type === 'issue-list') {
        issueOverviewListRef.current?.requestRefresh(options);
        return;
      }
      if (activeTabData?.type === 'need-export-list') {
        needExportListRef.current?.requestRefresh(options);
      }
    },
    [activeTabData?.type],
  );

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateFormData = (updates: Partial<InventoryIssueFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeIssueId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const openExistingIssue = async (row: InventoryIssueRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết phiếu xuất.', severity: 'error' });
      return;
    }

    if (detailLoading) return;

    setDetailLoading(true);
    let fullRow: InventoryIssueRow;
    try {
      fullRow = await getIssueById(row.id);
    } catch {
      fullRow = row;
    } finally {
      setDetailLoading(false);
    }

    guardAndRun(() => {
      const statusId = typeof fullRow.status === 'object' && fullRow.status !== null && fullRow.status.id
        ? fullRow.status.id
        : (fullRow.status_code || '');

      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: fullRow.code,
        type: 'update-issue',
        closable: true,
        entityId: fullRow.id,
        metadata: {
          companyId: fullRow.company_id,
          ...buildFormMetadata(
          {
            code: fullRow.code,
            name: fullRow.name,
            order_no: fullRow.order_no,
            order_id: fullRow.order_id || '',
            store_id: fullRow.warehouse_id || '',
            store_name: fullRow.store_name,
            status: statusId,
            amount: fullRow.amount ? String(fullRow.amount) : '',
            company_id: fullRow.company_id,
            note: fullRow.note || '',
            lines: (fullRow.lines || []).map((line, index) => ({
              id: index + 1,
              item_id: line.item_id,
              item_code: line.item_code,
              item_name: line.item_name,
              uom_id: line.uom_id,
              uom_name: line.uom_name,
              issued_qty: line.issued_qty,
              note: line.note,
            })),
          },
          fullRow.id
        ),
        },
      });
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = () => {
    refreshActiveList({ showSuccessMessage: true });
  };

  const handleChangeLine = (lineId: number, updates: Partial<InventoryIssueFormLine>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const nextLines = currentLines.map((line) =>
      line.id === lineId ? { ...line, ...updates } : line,
    );
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const handleAddLine = () => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const maxId = currentLines.reduce((max, line) => Math.max(max, line.id), 0);
    const newLine: InventoryIssueFormLine = {
      id: maxId + 1,
      item_code: '',
      item_name: '',
      uom_name: '',
      issued_qty: 0,
      note: '',
    };
    const nextFormData = { ...activeFormData, lines: [...currentLines, newLine] };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const handleRemoveLine = (lineId: number) => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const nextLines = currentLines.filter((line) => line.id !== lineId);
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const resolveStatusId = (statusValue: string, statusOptions: Array<{ id: string; name: string }>) => {
    if (!statusValue) return '';
    const matched = statusOptions.find((option) => option.id === statusValue);
    return matched?.id || statusValue;
  };

  const handleSave = () => {
    if (!activeFormData || !activeTabData?.id || saving) return;

    const isUpdateMode = Boolean(activeIssueId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật phiếu xuất.', severity: 'error' });
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo phiếu xuất.', severity: 'error' });
      return;
    }

    const normalizedStatusId = resolveStatusId(activeFormData.status, formStatusOptions);
    if (!normalizedStatusId) {
      setSnackbarMessage({ message: 'Không xác định được trạng thái hợp lệ để lưu phiếu xuất.', severity: 'error' });
      return;
    }

    const lines = (activeFormData.lines || []).map((line) => ({
      item: line.item_id || '',
      uom: line.uom_id || '',
      issued_qty: line.issued_qty || 0,
      note: line.note || '',
    }));

    const payload = {
      company: activeFormData.company_id,
      warehouse: activeFormData.store_id,
      status: normalizedStatusId,
      order: activeFormData.order_id || undefined,
      issue_no: activeFormData.code || undefined,
      issued_at: new Date().toISOString(),
      note: activeFormData.note || '',
      lines,
    };

    setSaving(true);
    void (async () => {
      try {
        let saved: any;
        if (isUpdateMode && activeIssueId) {
          saved = await updateIssue({ ...payload, id: activeIssueId });
        } else {
          saved = await createIssue(payload);
        }

        issueOverviewListRef.current?.requestRefresh();
        needExportListRef.current?.requestRefresh();
        handleUnsavedChangesUpdate(false, activeTabData.id);
        setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
        setSnackbarMessage({ message: `Đã lưu ${saved?.issue_no || activeFormData.code || activeFormData.name}.`, severity: 'success' });
        updateTab(activeTabData.id, {
          title: saved?.issue_no || activeFormData.code || activeFormData.name,
          metadata: buildFormMetadata(activeFormData, saved?.id || activeIssueId),
        });
      } catch (err: any) {
        const detail = err?.response?.data?.detail || err?.response?.data?.non_field_errors?.[0] || err?.message || 'Có lỗi xảy ra khi lưu phiếu xuất.';
        setSnackbarMessage({ message: typeof detail === 'string' ? detail : JSON.stringify(detail), severity: 'error' });
      } finally {
        setSaving(false);
      }
    })();
  };

  const createIssueStatus = async (payload: { name: string }) => {
    const isUpdateMode = Boolean(activeIssueId);
    const effectiveCompanyId = activeFormData?.company_id || selectedCompanyId;
    const tabPermissions = resolvePermissionsForCompanyId(effectiveCompanyId);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      throw new Error('Không có quyền tạo trạng thái.');
    }

    const nextStatusRaw = await createStatus({
      module: 'issue',
      name: payload.name.trim(),
    });

    const nextStatus: TransactionStatusOption = {
      id: nextStatusRaw.id || '',
      name: nextStatusRaw.name,
      color: nextStatusRaw.color,
      code: nextStatusRaw.code,
      is_default: nextStatusRaw.is_default,
      is_active: nextStatusRaw.is_active,
      sort_order: nextStatusRaw.sort_order,
    };

    setApiIssueStatuses((current) => {
      const existed = current.find(
        (option) => option.id === nextStatus.id || option.name.trim().toLowerCase() === nextStatus.name.trim().toLowerCase(),
      );
      if (existed) {
        return current;
      }
      return [...current, nextStatus];
    });

    return nextStatus;
  };

  const renderListContent = (tabType: string) => {
    if (tabType === 'issue-list') {
      return (
        <IssueOverviewList
          ref={issueOverviewListRef}
          companyId={listCompanyId}
          searchQuery={searchQuery}
          status={filters.status}
          onSelectRow={openExistingIssue}
          onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
          onRefreshSuccess={() => setSnackbarMessage({ message: 'Đã làm mới danh sách phiếu.', severity: 'success' })}
        />
      );
    }

    return (
      <IssueNeedExportList
        ref={needExportListRef}
        companyId={listCompanyId}
        searchQuery={searchQuery}
        status={filters.status}
        onSelectRow={openExistingIssue}
        onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
      />
    );
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryIssueFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {isListTabType(tab.type) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={toolbarStatusOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={renderListContent(tab.type)}
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <IssueForm
                        formData={tabFormData}
                        statusOptions={formStatusOptions}
                        warehouseOptions={warehouseOptions}
                        orderOptions={orderOptions}
                        orderLoading={orderLoading}
                        itemOptions={itemOptions}
                        uomOptionsByLineId={uomOptionsByLineId}
                        itemLoading={itemLoading}
                        stockByLineId={stockByLineId}
                        stockLoadingByLineId={stockLoadingByLineId}
                        stockErrorByLineId={stockErrorByLineId}
                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-issue' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.issueId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                        saving={saving}
                        onChangeLine={handleChangeLine}
                        onAddLine={handleAddLine}
                        onRemoveLine={handleRemoveLine}
                        canEdit={tab.type === 'new-issue' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        isDirty={!!dirtyTabIds[tab.id]}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert
          severity={snackbarMessage?.severity || 'success'}
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
